import React from 'react';
import t from 'tcomb-form';
import classNames from 'classnames';

import LoadingView from '../components/LoadingView';


/* Usage:

    In form model:

        fileField: FormFile,

      or

        imageField: FormImage,

    In form options:

        imageField: {
            factory: FileUpload,
        },

        fileField: {
            factory: ImageUpload,
        },
 */

const MAX_MB = 25;
const MAX_FILE_SIZE = MAX_MB * 1048576; // 25 MB


export class UploadedFile {
    constructor(fileName, dataUri, mimeType, size) {
        this.fileName = fileName;
        this.mimeType = mimeType;
        this.dataUri = dataUri;
        this.size = size;
    }

    static build(file) {
        return new Promise((resolve, reject) => {
            if (!file) {
                reject();
            }

            const fileReader = new FileReader();

            fileReader.onload = (upload) => {
                resolve(new UploadedFile(file.name, upload.target.result, file.type, file.size));
            };

            fileReader.readAsDataURL(file);
        });
    }

    isImage() {
        return this.mimeType && this.mimeType.indexOf('image/') !== -1;
    }

    isTooBig() {
        // Since the browser will choke if the file is bigger than 2MB (as datauri),
        // we don't render previews for bigger files

        return this.size > 2 * 1048576;
    }

    getImage() {
        if (this.isImage() && !this.isTooBig()) {
            return this.dataUri;
        } else {
            return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAD0UlEQVR4Xu2b3ZHTMBCAV1beuQ6Ix8ozUAG5CuAqgHRwV' +
                'EDogKuAXAXkKiBUwPFs3fjSQe7djphl5ENxpMSWJf8w0mNi/eyn3dXuWiZgaFmWvS6K4jMAzAHgwvSch983hJB1FEW3cRzvPIx/MCTRTZCm6ZwQ8sP35Of' +
                'GJ4QskyT5cu65Nv9rAXDOMwCYthnYVV8hxP1kMrn0pQ1HALIsmxZFgQCwPVFKp74mr0LinL8HgO8aeDtKKUK4dwW2HOcIgKr+Qoifs9kMfUBnjXMuDJOh' +
                'P/jEGFu5XMygAQghfhNCXqkCu/YLgwbAGCOc848A8K2y62tK6cKFaQ4eAAouzXINAC9KENI5XsVx/NjGJEYBAAWUcQlCeKkIvBNCXM1ms40thNEAkBAu8j' +
                'zHQOnALwDAwtY5jgpACaEoiq8A8KGy6yvG2KKpJlgDwHihoo51596eslv1GEQnaBo0TdMlIQRDdbVtKKXoF2qH0FYAKsFSXcGfn6OUxiYIdQHgYIYT4lFC' +
                'qBU0WQFomytQSt+YoromABTniE7w+YQAANQA9AvoNE82KwA44sPDw/V+v2+cJUZRtEuSBG1Y25oCKCHkeb7SBE3XSZLcnCJgDeAcWdv/bQCcOSFOOsf/B' +
                'kAJnHOOuULtE2JwANI0xXP+LQqEcX8URTdNvLp00FjLOEjnhRCXuoBpiAB0x5utRan97hhjmG4ftMEBwNWlaXqvifZaQ9DFFYMEICEssR5ZmkNr6QFAZ' +
                'wZWALIsuyiKAlPUxsfgGUGw8uMkzS3nUbXJGYC2gdApCCZnZasBqlN1BkBqAIaaampqu0a135ZSOm+b46sDegHgQtKuxggAlLjCmQl0tXsu5gkaEDTgX2' +
                'gdTECTD1gFQmUOXilCuDBZ2zGeTAUWLz5Alqh/2a7WRz9TlSkA8OUEUQvyPHedC1gpx2Qy2XVqAlar7KmTFxPoSRaraQMAXz7Aajt66BQ0IGiAh1BYFkQwE' +
                'BrETTIAwPeB+Lrt6KWoFxPwWRKzdROmUpoXAFIDVkKIQQRChJCdruYvq8vPL1pCNugyG7RV1a77eTGBroVoM18AEOIAD3FAG5Xsuu9oTADrC/v9/p0NoCiK' +
                '7kZfD1B3ygKC9t3/qOIAw9WWuixuGWN4qfqojcYE5G41/jbhVDlsVBpQd6ubPjcqDWgqXJ3nA4AQCIVAKKTD5U2zUA+oUw+ovPg01trqeOC+n5HXZrF' +
                '2+bdypftOwfTpLBYX1fv3fcviYv4tY+yoiGsCoPtWz8Uiehuj9mXpcoXyO14EYZWh9Sbp4cRPAIDfES1N2eIfdFB6ffgGMS8AAAAASUVORK5CYII=';
        }
    }

    renderPreview() {
        return (
            <span>
                <img src={this.getImage()} alt="" />
            </span>
        );
    }
}

export const FormFile = t.irreducible('FormFile', (x) => {
    return x instanceof UploadedFile && x.size < MAX_FILE_SIZE;
});

export const FormImage = t.subtype(FormFile, (x) => {
    return x.isImage();
}, 'FormImage');


class UploadWidget extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false
        }
    }

    render() {
        const type = this.props.uploadType;
        const locals = this.props.locals;

        const formGroupClasses = {
            'form-group': true,
            'form-control-upload': true,
            'has-error': locals.hasError
        };

        const formGroupStyle = { };

        const onChange = (e) => {
            this.setState({loading: true});
            const doClear = () => {
                Upload.clearFileInput(this.refs["fileInput"].getDOMNode());
            };

            UploadedFile.build(e.target.files[0]).then(uploadedFile => {
                locals.onChange(uploadedFile);
                doClear();
                this.setState({loading: false});
            }, doClear);
        };

        let helpText = locals.help || `Click to select your ${type}`;
        let imgElement;
        const uploadImgClasses = {"upload-img": true};

        if (locals.value && locals.value.isImage() && !locals.value.isTooBig() && type != 'file') {
            helpText = '';
            formGroupStyle.backgroundImage = `url(${locals.value.getImage()})`;
            formGroupStyle.backgroundRepeat = 'no-repeat';
            formGroupStyle.backgroundSize = 'contain';
            formGroupStyle.backgroundPosition = 'center center';
        } else {
            let imgStyle = {};

            if (locals.value) {
                helpText = locals.value.fileName;
                imgStyle.backgroundImage = `url(${locals.value.getImage()})`;
                uploadImgClasses['preview'] = true;
            }

            imgElement = (<div className={classNames(uploadImgClasses)} style={imgStyle}></div>);
        }

        const clearImg = (e) => {
                e.preventDefault();
                Upload.clearFileInput(this.refs["fileInput"].getDOMNode());
                locals.onChange(null);
        };

        if (locals.hasError && locals.value) {
            try {
                // Will raise an exception if file is too big
                FormFile(locals.value);

                // File size is OK, lets also check that it's an image
                /*if (type === 'image' && !locals.value.isImage()) {
                    helpText = 'Please select an image';
                }*/
            }

            catch(e) {
                helpText = `File is over ${MAX_MB} MB.`;
            }
        }

        let label;
        if (typeof locals.label != 'undefined' && locals.label != null){
            label = <h2 className="sub-title">{locals.label}</h2>
        }

        let closeButton = type == 'image' ? <button className="close" onClick={clearImg}><span>x</span></button> : '';

        return (
            <div>
                {label}
                <div className={classNames(formGroupClasses)} style={formGroupStyle}>
                    {closeButton}
                    <div className="form-control-upload-content">
                        {imgElement}
                        <span className="upload-msg">{helpText}</span>
                    </div>
                    {this.state.loading ? <LoadingView /> : null}
                    <input
                        disabled={locals.disabled}
                        name={locals.name}
                        placeholder={locals.placeholder}
                        onChange={onChange}
                        type="file"
                        ref="fileInput"
                    />
                </div>
            </div>
        );
    }
}


class Upload extends t.form.Component {
    static clearFileInput(ctrl) {
        try {
            ctrl.value = null;
        }
        catch(ex) { }
    }
}

export class FileUpload extends Upload {
    getTemplate() {
        return locals => (<UploadWidget locals={locals} uploadType='file' /> );
    }
}

export class ImageUpload extends Upload {
    getTemplate() {
        return locals => (<UploadWidget locals={locals} uploadType='image' /> );
    }
}



/** WEBPACK FOOTER **
 ** ./app/src/forms/FileUpload.js
 **/